import { API_CALL } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { FETCH_LOYALTY_POINTS_EARN_CALCULATION } from 'actions/apiActionConstants'
import { requestLoyaltyEarnPointsCalculationPostPurchase } from 'api/loyalty/requestLoyaltyEarnPointsCalculation'
import createBatchedPromise from 'api/createBatchedPromise'
import { requestLoyaltyEarnPointsCalculationBatch } from 'api/loyalty/requestLoyaltyEarnPointsCalculationBatch'
import type { EarnPointsCalculationOptions } from 'api/mappers/loyalty/mapLoyaltyEarnPointsCalculation'
import buildPointsKey from 'loyalty/lib/pointsCalculation/buildPointsKey'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getLoyaltyEarnRegion from 'loyalty/selectors/getLoyaltyEarnRegion'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'

interface BundledSingleArg {
  request: App.LoyaltyPointsEarnCalculationRequest
  key: string
}

/**
 * Bundles an individual earn calculation request into a single batch API call.
 * Requests are accumulated over 32ms and sent together.
 */
const bundleLoyaltyPointsEarnCalculationForBatchRequest = createBatchedPromise<
  Map<string, App.LoyaltyPointsEarnCalculation | undefined>,
  App.LoyaltyPointsEarnCalculation,
  BundledSingleArg,
  BundledSingleArg,
  string,
  EarnPointsCalculationOptions
>({
  waitTime: 32,
  argsReducerFn: (accumulated, singleArg) => ({
    ...accumulated,
    [singleArg.key]: singleArg,
  }),
  batchRequestFn: async(accumulated, options) => {
    const entries = Object.entries(accumulated)
    const requests = entries.map(([_, { request }]) => request)
    if (!options) {
      throw new Error('EarnPointsCalculationOptions are required for batch earn calculation')
    }
    const results = await requestLoyaltyEarnPointsCalculationBatch(requests, options)

    const resultMap = new Map<string, App.LoyaltyPointsEarnCalculation | undefined>()
    entries.forEach(([key], index) => {
      resultMap.set(key, results[index])
    })
    return resultMap
  },
  singleValueFn: (singleArg, resultMap) => {
    const result = resultMap.get(singleArg.key)
    if (!result) {
      throw new Error(`Loyalty earn calculation failed for key: ${singleArg.key}`)
    }
    return result
  },
})

function submitLoyaltyPointsEarnCalculationRequest(
  calculationRequest: App.LoyaltyPointsEarnCalculationRequest,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId ?? ''
    const currency = calculationRequest.currency ?? state.geo.currentCurrency
    const pointsEarnCalculationKey = buildPointsKey(calculationRequest, customerId)
    const existingCalculation = state.loyalty.pointsEarnCalculations[pointsEarnCalculationKey]
    const hasLuxPlusOrSubscriptionInCart = getHasLuxPlusOrSubscriptionInCart(state)
    const hasLoyaltyAccount = getHasLoyaltyAccount(state)
    const isCurrentRegionUserLoyaltyRegion = getIsCurrentRegionUserLoyaltyRegion(state)

    if (
      !getIsLoyaltyEnabled(state) ||
      (hasLoyaltyAccount && !isCurrentRegionUserLoyaltyRegion) ||
      existingCalculation?.data ||
      existingCalculation?.fetching ||
      existingCalculation?.error
    ) return

    const baseOptions = {
      customerId,
      currency,
      luxPlusMember: hasLuxPlusOrSubscriptionInCart,
      region: getLoyaltyEarnRegion(state),
    }

    // Use cart mode to determine if this is a post-purchase checkout
    const isPostPurchase = !!findPostPurchaseCheckout(state.checkout.cart.mode)

    const { orderItemId, orderId } = calculationRequest

    // Even though the request is for a post-purchase item, we still need to check if the orderItemId and orderId as post purchase is not garanteed to be an update operation
    const request = isPostPurchase && orderItemId && orderId
      ? () => requestLoyaltyEarnPointsCalculationPostPurchase(calculationRequest, {
          ...baseOptions,
          orderItemId: orderItemId,
          orderId: orderId,
        })
      : () => bundleLoyaltyPointsEarnCalculationForBatchRequest({ request: calculationRequest, key: pointsEarnCalculationKey }, baseOptions)

    dispatch({
      type: API_CALL,
      api: FETCH_LOYALTY_POINTS_EARN_CALCULATION,
      pointsEarnCalculationKey,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      request,
    })
  }
}

export default submitLoyaltyPointsEarnCalculationRequest
